import { getAxiosInstance, handleResponse, handleError, getOptionString, getSegmentOptions } from 'helpers/requestHandler'
import {
    AppMetaData,
    OrganizationData,
    BuilderData,
    AppData,
    Dict,
    GalleryData,
    UrlOption,
    SegmentOptions,
    MediaTagData,
    MapData,
    AdminOperation,
    CustomContentData,
} from 'app/types'
import { authHeader, getJWTKey } from 'helpers/authHeader'


export async function getAdmin(options) {
    return getAxiosInstance().get<AppMetaData>(`/admin${getOptionString(options)}`, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function getMarketingTracking(options) {
    return getAxiosInstance().get(`/admin/marketing-tracking${getOptionString(options)}`, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function saveMarketingTracking(data) {
    return getAxiosInstance().post('/admin/marketing-tracking', data, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function saveMarketingTrackingForms(forms) {
    return getAxiosInstance().post('/admin/marketing-tracking/forms', { forms }, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

/**
 * Data
 */
export async function postData(view: AdminView, data: OrganizationData | BuilderData | AppData, options: UrlOption) {
    let url = `/admin/${view}`
    if (options) {
        url += getOptionString(options)
    }
    return getAxiosInstance().post(url, data, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function getData(view: AdminView, options: Dict = null) {
    let url = `/admin/${view.singular()}`
    if (options != null) {
        url += getOptionString(options)
    }
    return getAxiosInstance().get(url, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function putData(view: AdminView, data: OrganizationData | BuilderData | AppData) {
    return getAxiosInstance().post(`/admin/${view}/${data.id}`, data, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function deleteData(view: AdminView, id: string) {
    return getAxiosInstance().delete(`/admin/${view}/${id}`, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function postAppData(app: AppData, view: AdminView, data: OrganizationData | BuilderData | AppData, options: UrlOption) {
    let url = `/admin/app/${app ? app.meta.id : null}/${view}`
    if (options) {
        url += getOptionString(options)
    }
    return getAxiosInstance().post(url, data, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function getAppData(app: AppData, view: AdminView) {
    return getAxiosInstance().get(`/admin/app/${app ? app.meta.id : null}/${view}`, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function putAppData(app: AppData, view: AdminView, data: OrganizationData | BuilderData | AppData) {
    return getAxiosInstance().post(`/admin/app/${app ? app.meta.id : null}/${view}/${data.id}`, data, authHeader())
        .then(handleResponse)
        .catch(handleError)
}


export async function deleteAppData(app: AppData, view: AdminView, id: string) {
    return getAxiosInstance().delete(`/admin/app/${app ? app.meta.id : null}/${view}/${id}`, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

// Org
export async function postOrganizationData(organization: OrganizationData, view: AdminView, data: OrganizationData | BuilderData | AppData, options: UrlOption) {
    let url = `/admin/organization/${organization?.id}/${view}`
    if (options) {
        url += getOptionString(options)
    }
    return getAxiosInstance().post(url, data, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function putOrganizationData(organization: OrganizationData, view: AdminView, data: OrganizationData | BuilderData | AppData) {
    return getAxiosInstance().post(`/admin/organization/${organization ? organization.id : null}/${view}/${data.id}`, data, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function deleteOrganizationData(organization: OrganizationData, view: AdminView, id: string) {
    return getAxiosInstance().delete(`/admin/organization/${organization ? organization.id : null}/${view}/${id}`, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function postMediaFiles(files, onUploadProgress: () => void, options: SegmentOptions, metadata?: Dict) {
    const formData = new FormData()
    files.forEach(file => formData.append('files[]', file))
    
    // Add metadata if provided
    if (metadata && Object.keys(metadata).length > 0) {
        formData.append('metadata', JSON.stringify(metadata))
    }
    
    return getAxiosInstance().post(`/admin/media-upload${getSegmentOptions(options)}`, formData, { ...authHeader(), 'Content-Type': 'multipart/form-data', onUploadProgress })
        .then(handleResponse)
        .catch(handleError)
}
export async function postMediaDownload(media, options: SegmentOptions) {
    return getAxiosInstance().post(`/admin/media-download${getSegmentOptions(options)}`, media, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function postMedia(media, options: SegmentOptions) {
    return getAxiosInstance().post(`/admin/media${getSegmentOptions(options)}`, media, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function putMedia(data: MediaData, options: SegmentOptions) {
    return getAxiosInstance().post(`/admin/media/${data.id}${getSegmentOptions(options)}`, data, authHeader())
        .then(handleResponse)
        .catch(handleError)
}
export async function deleteMedia(media: string[], options: SegmentOptions) {
    return getAxiosInstance().delete(`/admin/media/${media.join(',')}${getSegmentOptions(options)}`, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function getMediaThumbnails(data: MediaData, options: SegmentOptions) {
    return getAxiosInstance().get(`/admin/media/${data.id}/thumbnails${getSegmentOptions(options)}`, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function postMediaThumbnails(data: MediaData, options: SegmentOptions, file) {
    const formData = new FormData()
    formData.append("file", file) // Add the file to FormData

    // Add any additional data to FormData if necessary
    formData.append("data", JSON.stringify(data))

    return getAxiosInstance()
        .post(`/admin/media/${data.id}/thumbnails${getSegmentOptions(options)}`, formData, {
            headers: {
                ...authHeader().headers,
                "Content-Type": "multipart/form-data", // Ensure multipart form-data header
            },
        })
        .then(handleResponse)
        .catch(handleError)
}


export async function getMediaExport(data: MediaData, options: SegmentOptions) {
    return getAxiosInstance().get(`/admin/media/${data.id}/export${getSegmentOptions(options)}`, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function postMediaOptimize(data: MediaData, options: SegmentOptions) {
    return getAxiosInstance().post(`/admin/media/${data.id}/optimize${getSegmentOptions(options)}`, {}, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function postMap(map: MapData) {
    return getAxiosInstance().post('/admin/map', map, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function getMap(mapLink: string, options) {
    return getAxiosInstance().get(`/admin/map/${mapLink}${getSegmentOptions(options)}`)
        .then(handleResponse)
        .catch(handleError)
}

export async function putMap(map: MapData) {
    return getAxiosInstance().post(`/admin/map/${map.id}`, map, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function getJob(jobId: string) {
    return getAxiosInstance().get(`/admin/job/${jobId}`, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function postStartJob(jobId: string) {
    return getAxiosInstance().post(`/admin/job/${jobId}/start`, null, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function postMediaTag(data: MediaTagData, options: SegmentOptions) {
    return getAxiosInstance().post(`/admin/mediatag${getSegmentOptions(options)}`, data, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function putMediaTag(data: MediaTagData, options: SegmentOptions) {
    return getAxiosInstance().post(`/admin/mediatag/${data.id}${getSegmentOptions(options)}`, data, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function deleteMediaTag(data: SitemapData, options: SegmentOptions) {
    return getAxiosInstance().delete(`/admin/mediatag/${data.id}${getSegmentOptions(options)}`, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function postGallery(data: GalleryData, options: SegmentOptions) {
    return getAxiosInstance().post(`/admin/gallery${getSegmentOptions(options)}`, data, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function putGallery(data: GalleryData, options: SegmentOptions) {
    return getAxiosInstance().post(`/admin/gallery/${data.id}${getSegmentOptions(options)}`, data, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function deleteGallery(data: GalleryData, options: SegmentOptions) {
    return getAxiosInstance().delete(`/admin/gallery/${data.id}${getSegmentOptions(options)}`, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

// Logs
export async function getLogList(logType: LogType) {
    return getAxiosInstance().get(`/admin/log/${logType}`, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function getLog(logType: LogType, filename) {
    return getAxiosInstance().get(`/admin/log/${logType}/${filename}`, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function postRunOperation(operation: AdminOperation, data) {
    return getAxiosInstance().get(`/admin/operation/${operation}`, authHeader())
        // return getAxiosInstance().post(`/admin/operation/${operation}`, data, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function postAttributionSync(appId: string | number, options: { limit?: number, nocache?: boolean } = {}) {
    const params = new URLSearchParams()
    if (options.limit) params.set('limit', String(options.limit))
    if (options.nocache) params.set('nocache', '1')
    const qs = params.toString() ? `?${params.toString()}` : ''
    return getAxiosInstance().post(`/admin/app/${appId}/analytics/attribution/sync${qs}`, {}, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

// Custom content
export async function postContent(app: AppData, organization: OrganizationData, data: CustomContentData) {
    let url = ''
    if (app) {
        url = `/admin/app/${app.meta.id}/content`
    } else if (organization) {
        url = `/admin/organization/${organization.id}/content`
    }
    if (data.id != null) {
        url += `/${data.id}`
    }
    if (data.type != null) {
        data.dynamicContentTypeId = data.type
    }
    return getAxiosInstance().post(url, data, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function deleteContent(app: AppData, organization: OrganizationData, data: CustomContentData) {
    let url = ''
    if (app) {
        url = `/admin/app/${app.meta.id}/content/${data.id}`
    } else if (organization) {
        url = `/admin/organization/${organization.id}/content/${data.id}`
    }
    return getAxiosInstance().delete(url, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function postLLMContent(data: Dict, app: AppData, organization: OrganizationData) {
    return getAxiosInstance().post(`/content/prompt`, { ...data, appId: app?.meta.id, organizationId: organization?.id }, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

// Context
export async function getLLMContext(app: AppData, organization: OrganizationData) {
    return getAxiosInstance().get(`/admin/context?appId=${app?.meta.id ?? ''}&organizationId=${organization?.id ?? ''}`, authHeader())
}

export async function postLLMContext(data: Dict, app: AppData, organization: OrganizationData) {
    return getAxiosInstance().post(`/admin/context${data?.id ? `/${data.id}` : ''}`, { ...data, appId: app?.meta.id, organizationId: organization?.id }, authHeader())
}

export async function deleteLLMContext(data: Dict, app: AppData, organization: OrganizationData) {
    return getAxiosInstance().delete(`/admin/context/${data.id}?appId=${app?.meta.id ?? ''}&organizationId=${organization?.id ?? ''}`, authHeader())
}

// Analytics AI Context
export async function getAnalyticsAIContext(appId: string) {
    return getAxiosInstance().get(`/admin/analytics-ai-context?appId=${appId ?? ''}`, authHeader())
}

export async function saveAnalyticsAIContext(data: { app_id: string | null, scope: string, context: string }) {
    return getAxiosInstance().post('/admin/analytics-ai-context', data, authHeader())
}

export async function deleteAnalyticsAIContext(id: string) {
    return getAxiosInstance().delete(`/admin/analytics-ai-context/${id}`, authHeader())
}

// Location
export async function postLocation(app: AppData, organization: OrganizationData, data: Location) {
    let url = ''
    if (app) {
        url = `/admin/app/${app.meta.id}/location`
    } else if (organization) {
        url = `/admin/organization/${organization.id}/location`
    }
    if (data.id != null) {
        url += `/${data.id}`
    }
    return getAxiosInstance().post(url, data, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function deleteLocation(app: AppData, organization: OrganizationData, data: Location) {
    let url = ''
    if (app) {
        url = `/admin/app/${app.meta.id}/location/${data.id}`
    } else if (organization) {
        url = `/admin/organization/${organization.id}/location/${data.id}`
    }
    return getAxiosInstance().delete(url, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

/**
 * Documentation Link Services
 */
export async function getDocumentationLinks(): Promise<any> {
    return getAxiosInstance().get('/admin/documentation/links', authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function saveDocumentationLink(data: any): Promise<any> {
    return getAxiosInstance().post('/admin/documentation/links', data, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function deleteDocumentationLink(id: string): Promise<any> {
    return getAxiosInstance().delete(`/admin/documentation/links/${id}`, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

/**
 * Helper function to convert bloated date strings to compact YYYY-MM-DD format
 */
function optimizeDateInConfig(dateStr: string): string {
    if (!dateStr) return dateStr
    const date = new Date(dateStr)
    if (isNaN(date.getTime())) return dateStr
    return date.toISOString().split('T')[0] // Returns YYYY-MM-DD
}

/**
 * Helper function to expand compact dates back to ISO format for frontend
 */
function expandDateInConfig(dateStr: string): string {
    if (!dateStr) return dateStr
    // If it's already a full ISO string, return as-is
    if (dateStr.includes('T') || dateStr.includes('Z')) return dateStr
    // If it's a compact date (YYYY-MM-DD), expand to ISO format
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        const date = new Date(dateStr + 'T00:00:00.000Z')
        return date.toISOString()
    }
    return dateStr
}

/**
 * Optimize layout config for storage by compacting date formats
 */
function optimizeLayoutConfigForStorage(layoutConfig: any): string {
    if (!layoutConfig) return null
    
    const optimized = { ...layoutConfig }
    
    // Optimize date filter if present
    if (optimized.dateFilter) {
        if (optimized.dateFilter.startDate) {
            optimized.dateFilter.startDate = optimizeDateInConfig(optimized.dateFilter.startDate)
        }
        if (optimized.dateFilter.endDate) {
            optimized.dateFilter.endDate = optimizeDateInConfig(optimized.dateFilter.endDate)
        }
    }
    
    return JSON.stringify(optimized)
}

/**
 * Parse layout config from storage and expand dates for frontend use
 */
function parseLayoutConfigFromStorage(layoutConfigStr: string): any {
    
    if (!layoutConfigStr) {
        return null
    }
    
    try {
        const parsed = typeof layoutConfigStr === 'string' ? JSON.parse(layoutConfigStr) : layoutConfigStr
        
        // For date filter, keep dates in simple string format for DateRange component
        if (parsed.dateFilter) {
            
            if (parsed.dateFilter.startDate) {
                // Keep simple date format - DateRange expects strings like "2025-08-01"
                const startDate = new Date(parsed.dateFilter.startDate)
                parsed.dateFilter.startDate = startDate.toString()
            }
            if (parsed.dateFilter.endDate) {
                // Keep simple date format - DateRange expects strings  
                const endDate = new Date(parsed.dateFilter.endDate)
                parsed.dateFilter.endDate = endDate.toString()
            }
        }
        return parsed
    } catch (error) {
        console.error('❌ parseLayoutConfigFromStorage - Parse error:', error)
        console.warn('Failed to parse layout config:', error)
        return null
    }
}

/**
 * Analytics Dashboard Services
 */
export async function getAnalyticsDashboards(appId?: string, organizationId?: string): Promise<any> {
    const queryParams = []
    if (appId) queryParams.push(`appId=${appId}`)
    if (organizationId) queryParams.push(`organizationId=${organizationId}`)

    const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : ''

    return getAxiosInstance().get(`/admin/analytics/dashboard${queryString}`, authHeader())
        .then(response => {
            // Parse and expand layout_config for each dashboard
            if (response.dashboards && Array.isArray(response.dashboards)) {
                response.dashboards = response.dashboards.map(dashboard => ({
                    ...dashboard,
                    layoutConfig: parseLayoutConfigFromStorage(dashboard.layout_config)
                }))
            }
            return response
        })
        .then(handleResponse)
        .catch(handleError)
}

export async function getAnalyticsDashboard(dashboardId: string): Promise<any> {
    return getAxiosInstance().get(`/admin/analytics/dashboard/${dashboardId}`, authHeader())
        .then(response => {
            // Parse and expand layout_config
            if (response.dashboard && response.dashboard.layout_config) {
                response.dashboard.layoutConfig = parseLayoutConfigFromStorage(response.dashboard.layout_config)
            }
            return response
        })
        .then(handleResponse)
        .catch(handleError)
}

export async function postAnalyticsDashboard(data: any): Promise<any> {
    // Optimize layout config before sending
    const dataToSend = {
        ...data,
        layoutConfig: optimizeLayoutConfigForStorage(data.layoutConfig)
    }
    
    return getAxiosInstance().post('/admin/analytics/dashboard', dataToSend, authHeader())
        .then(response => {
            // Parse and expand layout_config in response
            if (response.dashboard && response.dashboard.layout_config) {
                response.dashboard.layoutConfig = parseLayoutConfigFromStorage(response.dashboard.layout_config)
            }
            return response
        })
        .then(handleResponse)
        .catch(handleError)
}

export async function putAnalyticsDashboard(dashboardId: string, data: any): Promise<any> {
    // Optimize layout config before sending
    const dataToSend = {
        ...data,
        layoutConfig: optimizeLayoutConfigForStorage(data.layoutConfig)
    }
    
    return getAxiosInstance().post(`/admin/analytics/dashboard/${dashboardId}`, dataToSend, authHeader())
        .then(response => {
            // Parse and expand layout_config in response
            if (response.dashboard && response.dashboard.layout_config) {
                response.dashboard.layoutConfig = parseLayoutConfigFromStorage(response.dashboard.layout_config)
            }
            return response
        })
        .then(handleResponse)
        .catch(handleError)
}

export async function setAnalyticsDashboardAsDefault(dashboardId: string, appId?: string, organizationId?: string): Promise<any> {
    const queryParams = []
    if (appId) queryParams.push(`appId=${appId}`)
    if (organizationId) queryParams.push(`organizationId=${organizationId}`)

    const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : ''

    return getAxiosInstance().post(`/admin/analytics/dashboard/${dashboardId}/set-default${queryString}`, {}, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function deleteAnalyticsDashboard(dashboardId: string): Promise<any> {
    return getAxiosInstance().delete(`/admin/analytics/dashboard/${dashboardId}`, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

// Dashboard Tile operations
export async function getTiles(dashboardId: string) {
    const options = { dashboardId }
    return getAxiosInstance().get<{ tiles: AnalyticsDashboardTile[] }>(`/admin/analytics/dashboard-tile${getOptionString(options)}`, authHeader())
        .then(response => {
            // Parse config from string to object for each tile
            if (response.tiles && Array.isArray(response.tiles)) {
                response.tiles = response.tiles.map(tile => ({
                    ...tile,
                    config: typeof tile.config === 'string' ? JSON.parse(tile.config) : tile.config
                }))
            }
            return response
        })
        .then(handleResponse)
        .catch(handleError)
}

export async function getTile(tileId: string) {
    return getAxiosInstance().get<{ tile: AnalyticsDashboardTile }>(`/admin/analytics/dashboard-tile/${tileId}`, authHeader())
        .then(response => {
            // Parse config from string to object
            if (response.tile && response.tile.config) {
                response.tile = {
                    ...response.tile,
                    config: typeof response.tile.config === 'string' ? JSON.parse(tile.config) : tile.config
                }
            }
            return response
        })
        .then(handleResponse)
        .catch(handleError)
}

export async function postTile(tile: AnalyticsDashboardTile) {
    // Convert config from object to string if needed
    const tileToSend = {
        ...tile,
        config: tile.config ? (typeof tile.config === 'string' ? tile.config : JSON.stringify(tile.config)) : null
    }
   
    return getAxiosInstance().post<{ tile: AnalyticsDashboardTile }>('/admin/analytics/dashboard-tile', tileToSend, authHeader())
        .then(response => {
            // Parse config from string to object in the response
            if (response.tile && response.tile.config) {
                response.tile = {
                    ...response.tile,
                    config: typeof response.tile.config === 'string' ? JSON.parse(tile.config) : tile.config
                }
            }
            return response
        })
        .then(handleResponse)
        .catch(handleError)
}

export async function putTile(tileId: string, tile: AnalyticsDashboardTile) {
    // Convert config from object to string if needed
    const tileToSend = {
        ...tile,
        config: tile.config ? (typeof tile.config === 'string' ? tile.config : JSON.stringify(tile.config)) : null
    }

    return getAxiosInstance().post<{ tile: AnalyticsDashboardTile }>(`/admin/analytics/dashboard-tile/${tileId}`, tileToSend, authHeader())
        .then(response => {
            // Parse config from string to object in the response
            if (response.tile && response.tile.config) {
                response.tile = {
                    ...response.tile,
                    config: typeof response.tile.config === 'string' ? JSON.parse(tile.config) : tile.config
                }
            }
            return response
        })
        .then(handleResponse)
        .catch(handleError)
}

export async function deleteTile(tileId: string) {
    return getAxiosInstance().delete<{ id: string }>(`/admin/analytics/dashboard-tile/${tileId}`, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

// Batch operations for tile positioning
export async function updateTilePositions(tiles: AnalyticsDashboardTile[]) {
    return getAxiosInstance().post<{ tiles: AnalyticsDashboardTile[] }>('/admin/analytics/dashboard-tile/batch', { tiles }, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

// Retrieve marketing spend data by year
export async function getMarketingSpend(options: SegmentOptions = {}, year=new Date().getFullYear(), channel?: string) {
    let url = `/admin/marketing-spend${getSegmentOptions(options)}&year=${year}`
    if (channel) {
        url += `&channel=${encodeURIComponent(channel)}`
    }
    return getAxiosInstance().get(url, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

// Upsert marketing spend data
export async function putMarketingSpend(data: Dict, options: Dict) {
    return getAxiosInstance().post(`/admin/marketing-spend?appId=${options?.app?.meta?.id ?? ''}`, data, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

// Import marketing spend data from CSV rows
export async function importMarketingSpend(rows: Dict[], app: AppData, importLabel?: string) {
    return getAxiosInstance().post(`/admin/marketing-spend/import?appId=${app?.meta?.id ?? ''}`, { rows, import_label: importLabel ?? null }, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

// Retrieve import history (grouped by batch) for an app
export async function getMarketingSpendHistory(app: AppData) {
    return getAxiosInstance().get(`/admin/marketing-spend/history${getSegmentOptions({ app })}`, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

// Retrieve all rows for a specific import batch
export async function getMarketingSpendBatch(app: AppData, batchId: string) {
    return getAxiosInstance().get(`/admin/marketing-spend/history/${batchId}${getSegmentOptions({ app })}`, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

// Delete an entire import batch (revert)
export async function deleteMarketingSpendBatch(app: AppData, batchId: string) {
    return getAxiosInstance().delete(`/admin/marketing-spend/history/${batchId}${getSegmentOptions({ app })}`, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

// Delete a single spend entry
export async function deleteMarketingSpendEntry(app: AppData, entryId: string) {
    return getAxiosInstance().delete(`/admin/marketing-spend/entry/${entryId}${getSegmentOptions({ app })}`, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

// Update a single spend entry's amount/impressions
export async function updateMarketingSpendEntry(app: AppData, entryId: string, amount: number, impressions?: number | null) {
    return getAxiosInstance().post(`/admin/marketing-spend/entry/${entryId}${getSegmentOptions({ app })}`, { amount, impressions: impressions ?? null }, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

// Sales Tracking API functions
export async function getSalesTracking(app: AppData) {
    return getAxiosInstance().get(`/admin/sales-tracking?appId=${app?.meta?.id ?? ''}`, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function postSalesTracking(data: any, app: AppData) {
    return getAxiosInstance().post(`/admin/sales-tracking?appId=${app?.meta?.id ?? ''}`, data, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function postSalesTrackingBulk(salesTrackingArray: any[], app: AppData) {
    const payload = { sales_tracking: salesTrackingArray }
    return getAxiosInstance().post(`/admin/sales-tracking?appId=${app?.meta?.id ?? ''}`, payload, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function putSalesTracking(id: string, data: any, app: AppData) {
    return getAxiosInstance().post(`/admin/sales-tracking/${id}?appId=${app?.meta?.id ?? ''}`, data, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function deleteSalesTracking(id: string, app: AppData) {
    return getAxiosInstance().delete(`/admin/sales-tracking/${id}?appId=${app?.meta?.id ?? ''}`, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

// Search users associated with project for customer linking
export async function searchProjectUsers(searchTerm: string, app: AppData) {
    // Validate required parameters
    if (!searchTerm || searchTerm.trim().length === 0) {
        throw new Error('Search term is required')
    }
    
    // Handle both app.meta.id (from admin.project) and app.id patterns
    let appId = null
    if (app?.meta?.id) {
        appId = app.meta.id
    } else if (app?.id) {
        appId = app.id
    } else {
        throw new Error('Valid app with id is required')
    }

    const params = new URLSearchParams({
        appId: appId,
        q: searchTerm.trim()
    })
    
    return getAxiosInstance().get(`/admin/sales-tracking/users/search?${params}`, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

// Get user details by ID for sales tracking
export async function getUserById(userId: string) {
    if (!userId || userId.trim().length === 0) {
        throw new Error('User ID is required')
    }

    return getAxiosInstance().get(`/admin/sales-tracking/user/${userId.trim()}`, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function postDemoApp(link: string) {
    // Get the JWT key and token for debugging
    const jwtKey = getJWTKey()
    const token = localStorage.getItem(jwtKey)
    
    const authHeaderResult = authHeader()
    
    return getAxiosInstance().post(`/admin/demo/${link}`, {}, authHeaderResult)
        .then(response => {
            return handleResponse(response)
        })
        .catch(error => {
            return handleError(error)
        })
}

export async function postTemplateClone(pageId: string) {
    return getAxiosInstance().post(`/admin/template/${pageId}`, {}, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function postTemplateCloneToApp(pageId: string, appId: string, usePlaceholders?: boolean) {
    const data = usePlaceholders ? { usePlaceholders } : {}
    return getAxiosInstance().post(`/admin/template/${pageId}/app/${appId}`, data, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function postTemplateCloneToOrganization(pageId: string, organizationId: string, usePlaceholders?: boolean) {
    const data = usePlaceholders ? { usePlaceholders } : {}
    return getAxiosInstance().post(`/admin/template/${pageId}/organization/${organizationId}`, data, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

/**
 * Get template pages
 * Fetches all pages marked as templates (site-wide, not filtered by organization)
 */
export async function getTemplatePages() {
    return getAxiosInstance().get('/admin/templates', authHeader())
        .then(handleResponse)
        .catch(handleError)
}

/**
 * Get HubSpot meetings for a project
 */
export async function syncHubspotMeetings(app: AppData) {
    return getAxiosInstance().post(`/admin/app/${app.meta.id}/meetings/sync`, {}, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function getHubspotMeetings(app: AppData, options: {
    filter?: string
    page?: number
    limit?: number
    q?: string
    searchScope?: string
    outcome?: string
    prefix?: string
} = {}) {
    const params = new URLSearchParams()
    if (options.filter) params.append('filter', options.filter)
    if (options.page) params.append('page', String(options.page))
    if (options.limit) params.append('limit', String(options.limit))
    if (options.q) params.append('q', options.q)
    if (options.searchScope) params.append('searchScope', options.searchScope)
    if (options.outcome) params.append('outcome', options.outcome)
    if (options.prefix) params.append('prefix', options.prefix)
    const queryString = params.toString() ? `?${params.toString()}` : ''
    return getAxiosInstance().get(`/admin/app/${app.meta.id}/meetings${queryString}`, authHeader())
        .then(handleResponse)
        .catch(handleError)
}

export async function getHubspotContactDetails(app: AppData, contactId: string) {
    return getAxiosInstance().get(`/admin/app/${app.meta.id}/meetings/contact/${contactId}`, authHeader())
        .then(handleResponse)
        .catch(handleError)
}
